/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * dailyTasks.js
 *
 * Pure helpers for the "Today's tasks" (Mai feladatok) block shared by the Time
 * Tracker screen and the home-screen widgets. No React, no network. The mobile
 * twin of the admin panel's `features/time-tracking/dailyTasks.js`.
 *
 * @module helpers/dailyTasks
 */

/** Rows the home-screen widget snapshot carries (the views render fewer when small). */
export const DAILY_TASKS_WIDGET_MAX = 5;

const BUCKET_RANK = {overdue: 0, today: 1};

/**
 * Stable overdue-first ordering (server order kept inside a bucket).
 * @param {object[]} tasks Normalised daily tasks.
 * @returns {object[]} A new, ordered array.
 */
export const orderDailyTasks = (tasks) => (Array.isArray(tasks) ? tasks : [])
    .map((task, index) => ({task, index}))
    .sort((a, b) => ((BUCKET_RANK[a.task?.bucket] ?? 1) - (BUCKET_RANK[b.task?.bucket] ?? 1)) || a.index - b.index)
    .map(({task}) => task);

/**
 * "CODE · Project" sub-line; either half may be missing.
 * @param {{code?: string, projectTitle?: string}} task Daily task.
 * @returns {string}
 */
export const dailyTaskMeta = (task) => [task?.code, task?.projectTitle].filter(Boolean).join(' · ');

/**
 * Whether `task` is the one the running timer is booked on.
 * @param {{_id?: string}} task Daily task.
 * @param {{taskId?: string}|null} [timer] Unified timer (contract C1).
 * @returns {boolean}
 */
export const isActiveDailyTask = (task, timer) => !!timer?.taskId && !!task?._id && String(task._id) === String(timer.taskId);

/**
 * The `startTracking` target for a daily task (a project timer on its task).
 * @param {{_id: string, projectId: string}} task Daily task.
 * @returns {{kind: 'project', id: string, taskId: string}}
 */
export const dailyTaskToTarget = (task) => ({kind: 'project', id: String(task.projectId), taskId: String(task._id)});

/**
 * Compact rows for the widget snapshot: only startable tasks (those with a
 * project), overdue first, capped.
 * @param {object[]} tasks Normalised daily tasks.
 * @param {number} [max] Row cap.
 * @returns {Array<{id: string, taskId: string, projectId: string, title: string, projectTitle: string, bucket: 'overdue'|'today'}>}
 */
export const toWidgetDailyTasks = (tasks, max = DAILY_TASKS_WIDGET_MAX) => orderDailyTasks(tasks)
    .filter((task) => task?._id && task.projectId)
    .slice(0, Math.max(0, max))
    .map((task) => ({
        id: String(task._id),
        taskId: String(task._id),
        projectId: String(task.projectId),
        title: String(task.title || ''),
        projectTitle: String(task.projectTitle || ''),
        bucket: task.bucket === 'overdue' ? 'overdue' : 'today',
    }));
